import React, { useEffect, useRef, useState } from 'react';
import Select, { type OnChangeValue, type SelectInstance, type StylesConfig } from 'react-select';
import type { FilterOptionOption } from 'react-select/dist/declarations/src/filters';
import styled, { css } from 'styled-components';

import type { DepartureOption, DepartureOptions } from '@lt/api/dist/types/departure';

import { isIOS, isTouchScreen } from '../../../../utils';
import { convertLatinToRus } from '../../utils';

import type { InputActionMeta } from 'react-select/dist/declarations/src/types';
import { useEventListener } from 'usehooks-ts';
import {
  mobileDefaultControlStyles,
  mobileHotelPageControlStyles,
  MobileLabel,
  mobileWidgetInputStyles,
} from '../../styles';
import type { MobileProps } from '../../types';
import Option from './components/Option';
import DropdownIndicator from './components/DropdownIndicator';
import NoOptionsMessage from './components/NoOptionsMessage';
import LoadingMessage from './components/LoadingMessage';
import Control from './components/Control';
import Input from './components/Input';
import SingleValue from './components/SingleValue';
import ValueContainer from './components/ValueContainer';
import { departurePickerName, departurePlaceholder } from './constants';
import CloseButton from '../CloseButton';

export type DeparturePickerProps = {
  options: DepartureOptions;
  menuIsOpen?: boolean;
  isDisabled?: boolean;
  isLoading?: boolean;
  onChange?: (newValue: OnChangeValue<DepartureOption, false>, actionMeta: any) => void;
  value?: DepartureOption;
  inputValue?: string;
  onInputChange?: (newValue: string, actionMeta: InputActionMeta) => void;
  onMenuOpen?: () => void;
  onMenuClose?: () => void;
  className?: string;
  variant?: 'default' | 'hotel';
  isWidget?: boolean;
} & MobileProps;

export const DeparturePicker = ({
  options,
  isMobile,
  onChange,
  menuIsOpen,
  isDisabled,
  isLoading,
  isWizard,
  value,
  onMenuOpen,
  onMenuClose,
  inputValue,
  onInputChange,
  className,
  variant = 'default',
  isWidget = false,
}: DeparturePickerProps): JSX.Element => {
  const ref = useRef<SelectInstance<DepartureOption>>(null);
  const [inputValueChanged, setInputValueChanged] = useState(false);

  const filterOptions = (
    { value: optionValue, label, data: { selectedLabel } }: FilterOptionOption<DepartureOption>,
    currentValue: string
  ) => {
    if (!inputValueChanged) return true;

    const lowerInputValue = currentValue.toLowerCase();
    const lowerValue = optionValue.toLowerCase();
    const lowerLabel = label.toLowerCase();
    const lowerSelectedLabel = selectedLabel.toLowerCase();
    const wrongKeyboardWord = convertLatinToRus(currentValue);

    if (currentValue) {
      return (
        lowerValue.includes(lowerInputValue) ||
        lowerLabel.includes(lowerInputValue) ||
        lowerLabel.includes(wrongKeyboardWord) ||
        lowerSelectedLabel.includes(lowerInputValue) ||
        lowerSelectedLabel.includes(wrongKeyboardWord)
      );
    }
    return true;
  };

  useEffect(() => {
    if (menuIsOpen && ref.current) {
      ref.current.focus();
      if (isIOS && !isWidget) {
        setTimeout(() => {
          // Хак из-за того, что из-за клавиатуры инпут на айфоне улетает вверх
          // TODO: Убрать это и сделать нормально
          window.scrollTo(0, 0);
        }, 500);
      }
    }
  }, [isWidget, menuIsOpen]);

  // Если тыкаем где-то за пределами инпута, то скрываем клавиатуру на мобильном устройстве
  useEventListener('touchstart', (event) => {
    const el = ref.current?.controlRef;

    if (!el || el.contains(event.target as Node)) {
      return;
    }

    setTimeout(() => {
      ref.current?.inputRef?.blur();
    }, 200);
  });

  const handleInputChange = (newValue: string, actionMeta: InputActionMeta) => {
    onInputChange && onInputChange(newValue, actionMeta);

    const { action } = actionMeta;

    if (action === 'set-value') {
      setInputValueChanged(false);
    }

    if (action === 'input-change') {
      setInputValueChanged(true);
    }
  };

  // По клику в инпут сдвигаем мигающий курсор в конец строки,
  // если пользователь не менял содержимое инпута
  const onFocus = () => {
    const inputRef = ref.current?.inputRef;
    if (!inputRef) return;

    value && inputRef.select();

    if (inputValueChanged) return;

    setTimeout(() => {
      inputRef.setSelectionRange(inputRef.value.length, inputRef.value.length);
    }, 0);
  };

  // Запрещаем выделять текст в инпуте по клику, если пользователь
  // не менял содержимое инпута
  const selectStyles: StylesConfig<DepartureOption> = {
    input: (styles) => ({
      ...styles,
      ...(!inputValueChanged && {
        userSelect: 'none',
        WebkitUserSelect: 'none',
        msUserSelect: 'none',
      }),
    }),
  };

  return (
    <>
      {isMobile && !isWizard && (
        <StyledMobileLabel $variant={variant}>{departurePlaceholder}</StyledMobileLabel>
      )}
      <SelectContainer
        $isMobile={!!isMobile}
        $isWizard={!!isWizard}
        className={className}
        $withCloseButton={menuIsOpen && isMobile && !isWizard}
        $variant={variant}
        $isWidget={isWidget}
      >
        {isMobile && !isWizard && menuIsOpen && <StyledCloseButton onClick={onMenuClose} />}
        <Select<DepartureOption>
          ref={ref}
          options={options}
          defaultValue={!isWizard ? value : undefined}
          inputValue={!isTouchScreen || (isTouchScreen && menuIsOpen) ? inputValue : undefined}
          /**
           * Нужно, чтобы при очищении поля в нём не оставалось
           * ранее выбранное значение
           */
          value={inputValue ? value : null}
          className={departurePickerName}
          classNamePrefix={departurePickerName}
          placeholder={departurePlaceholder}
          isMulti={false}
          isClearable
          backspaceRemovesValue
          escapeClearsValue={false}
          components={{
            Option,
            Control,
            NoOptionsMessage,
            LoadingMessage,
            Input,
            DropdownIndicator: isTouchScreen ? null : DropdownIndicator,
            IndicatorSeparator: () => null,
            ClearIndicator: () => null,
            ...(!isTouchScreen ? { SingleValue: () => null } : { SingleValue }),
            ValueContainer,
          }}
          isLoading={isLoading}
          onChange={onChange}
          onFocus={onFocus}
          onInputChange={handleInputChange}
          onMenuOpen={onMenuOpen}
          onMenuClose={isTouchScreen && !isWizard ? undefined : onMenuClose}
          menuIsOpen={menuIsOpen}
          menuShouldScrollIntoView={false}
          isDisabled={isDisabled}
          isSearchable={!isTouchScreen || menuIsOpen}
          filterOption={filterOptions}
          aria-label={departurePlaceholder}
          styles={selectStyles}
        />
      </SelectContainer>
    </>
  );
};

const SelectContainer = styled.div<{
  $isMobile: boolean;
  $isWizard: boolean;
  $isWidget: boolean;
  $withCloseButton?: boolean;
  $variant?: 'default' | 'hotel';
}>`
  position: relative;

  .${departurePickerName} {
    font-size: 14px;
    line-height: 16px;
    color: #17181a;

    &__control {
      min-width: 150px;
      padding: 8px;
      border: 0;
      box-shadow: none;
      height: 56px;
      caret-color: #007aff;
      border-radius: 4px 0 0 4px;
      font-weight: bold;
      color: inherit;

      ${({ $isMobile, $isWizard }) =>
        $isMobile &&
        !$isWizard &&
        `&:hover {
        border: none;
      }`}

      &--menu-is-open {
        font-weight: normal;

        & .${departurePickerName}__indicator {
          transform: rotate(180deg);
        }
      }
    }

    &__input-container:not([data-value='']) {
      padding: ${({ $isMobile }) => ($isMobile ? '0 0 0 24px' : '0 0 0 18px')};
      &::before {
        content: 'из';
        position: absolute;
      }
    }

    ${({ $isMobile }) =>
      !$isMobile &&
      `
       /* fix small input touch bug */
       &__input-container {
         display: block;

         & > input {
           display: inline-block;
         }

         &::after {
           content: none;
         }

         &::before {
          left: 0;
         }
       }
     `}

    &__menu {
      min-width: 245px;
      margin: 0;
      padding-top: 8px;
      box-shadow: 0 2px 4px rgb(210 208 197 / 80%);

      @media (min-width: 1200px) {
        width: 320px;
      }

      &:after {
        content: '';
        height: 30px;
        position: absolute;
        left: 0;
        bottom: 0;
        right: 0;
        background: linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, #ffffff 100%);
        border-radius: 4px;
      }

      &-list {
        padding: 0;
      }
    }

    &__option {
      padding: 4px 8px 8px;
      cursor: pointer;

      &:last-of-type {
        padding-bottom: 24px;
      }

      &--is-selected {
        background-color: #e6f2ff;
      }
    }

    &__single-value {
      display: flex;
      align-items: center;
      color: inherit;
    }

    &__value-container {
      padding: 0;

      &:after {
        content: '';
        width: 24px;
        position: absolute;
        top: 0;
        bottom: 0;
        right: 0;
        background: linear-gradient(90deg, rgba(255, 255, 255, 0) 0%, #ffffff 100%);
      }
    }

    &__placeholder {
      color: #919499;
      font-weight: normal;
    }

    &__indicator {
      padding: 0;
    }

    &__icon-search {
      display: none;
    }

    ${({ $isWizard }) =>
      $isWizard &&
      css`
        &__control {
          margin-top: 0;
        }
      `};
  }

  ${({ $isMobile, $variant }) =>
    $isMobile &&
    css`
      width: 100%;

      .${departurePickerName} {
        &__control {
          ${mobileDefaultControlStyles}
          ${$variant === 'hotel' && mobileHotelPageControlStyles}
        }

        &__menu {
          height: 100vh;
          max-width: 100%;
          left: 0;
          border-radius: 0;

          &-list {
            padding-bottom: 60px;
          }
        }

        &__placeholder {
          color: #c2c5cc;
          font-weight: normal;
        }
      }
    `};

  ${({ $isWizard }) =>
    $isWizard &&
    css`
      flex-grow: 0;

      .${departurePickerName} {
        &__control {
          max-width: 100%;
          background-color: transparent;
          padding: 0;
          min-height: initial;
          height: initial;
          border: none;
          font-weight: normal;

          &--menu-is-open {
            border: 1px solid #c2c5cc;
            border-radius: 4px;
            background-color: #fff;
            margin: 8px;
            padding: 14px 8px;

            .${departurePickerName}__value-container {
              justify-content: start;
            }

            .${departurePickerName}__icon-search {
              display: block;
            }
          }
        }

        // зажимаем высоту списка, 144px высота инпута departure в открытом состоянии + высота destination в закрытом
        &__menu-list {
          height: calc(100vh - 144px);
          max-height: initial;
        }

        &__value-container {
          justify-content: center;
        }

        &__input-container {
          padding: 0;
          margin: 0;
        }

        &__single-value--is-disabled {
          color: inherit;
        }
      }
    `};

  ${({ $withCloseButton, $variant }) =>
    $withCloseButton &&
    css`
      position: fixed;
      // Костыль для position: fixed на айфон
      transform: translate3d(0, 0, 0);
      top: 0;
      left: 0;
      right: 0;
      z-index: ${$variant === 'hotel' ? '10001' : '1'};
      background-color: #f5f4f2;

      .${departurePickerName} {
        padding: 8px 40px 8px 8px;

        &__control {
          border: none;
        }

        &__menu {
          width: 100%;
          right: 0;
          left: 0;
          background-color: #f5f4f2;
          position: absolute;
          z-index: 1;
          padding-top: 0px;

          &-list {
            // Из высоты экрана вычитаем высоту инпута
            height: calc(100vh - ${isIOS ? '150px' : '64px'});
            max-height: none;
            background-color: #fff;
            padding-top: 8px;
            border-radius: 8px 8px 0 0;
            overflow: scroll;
          }
        }

        &__icon-search {
          display: inline;
        }
      }
    `};

  ${({ $isMobile, $isWidget, $isWizard }) =>
    $isWidget && $isMobile && !$isWizard && mobileWidgetInputStyles}
`;

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
const StyledCloseButton = styled(CloseButton).attrs({ $inMobileInput: true })`
  margin-top: 24px;
  margin-right: 12px;
`;

const StyledMobileLabel = styled(MobileLabel)`
  margin-bottom: 4px;
`;
